import React, { useEffect, useState } from 'react';
import { Box, Typography, styled } from '@mui/material';
import { css, Global } from '@emotion/react';
import { observer } from 'mobx-react';
import {
    calculateTimeDifference,
    type TimeRemaining,
} from '../../utils/timeUtils';
import { countdownStore } from '../../stores/CountdownStore';
import squadaOneFont from '../../assets/fonts/SquadaOne-Regular.ttf';
import countDownBg from '../../assets/images/count-down-bg.png';

interface CountDownProps {
    targetDate: Date;
    onComplete?: () => void;
}

const GlobalFontStyles = () => (
    <Global
        styles={css`
            @font-face {
                font-family: 'SquadaOne';
                src: url(${squadaOneFont}) format('truetype');
                font-weight: normal;
                font-style: normal;
                font-display: block;
            }

            .squadaone-font {
                font-family: 'SquadaOne', monospace !important;
            }
        `}
    />
);

const NumberBlock = styled(Box)(({ theme }) => ({
    height: 64,
    width: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: `url(${countDownBg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    borderRadius: '8px',
    '& span': {
        fontSize: 32,
        fontWeight: 'normal',
        lineHeight: 'normal',
        color: '#FAD218',
        textRendering: 'geometricPrecision',
        WebkitFontSmoothing: 'antialiased',
    },
}));

const TimeSection = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    '& .number-group': {
        display: 'flex',
        gap: 2,
    },
    '& .label': {
        fontFamily: 'Clear Sans',
        fontSize: 14,
        fontWeight: 'normal',
        color: '#FFFFFF',
    },
});

const Separator = styled(Box)({
    margin: '0 4px',
    height: 48,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
});

const MobileCountDown = observer(
    ({ targetDate, onComplete }: CountDownProps) => {
        const isValidDate = !isNaN(new Date(targetDate).getTime());
        const [fontLoaded, setFontLoaded] = useState(false);

        const [time, setTime] = useState<TimeRemaining>(
            isValidDate
                ? calculateTimeDifference(targetDate)
                : {
                      days: '00',
                      hours: '00',
                      minutes: '00',
                      seconds: '00',
                      timeDifference: 0,
                  },
        );

        useEffect(() => {
            const loadFont = async () => {
                try {
                    const font = new FontFace(
                        'SquadaOne',
                        `url(${squadaOneFont})`,
                    );
                    await font.load();
                    document.fonts.add(font);
                    setFontLoaded(true);
                } catch (error) {
                    setFontLoaded(true);
                }
            };

            loadFont();
        }, []);

        useEffect(() => {
            if (!isValidDate) {
                setTime({
                    days: '00',
                    hours: '00',
                    minutes: '00',
                    seconds: '00',
                    timeDifference: 0,
                });
                return;
            }

            const temp = calculateTimeDifference(targetDate);
            if (temp.timeDifference > 0) {
                const timer = setInterval(() => {
                    const tmp = calculateTimeDifference(targetDate);
                    if (tmp.timeDifference <= 0) {
                        onComplete?.();
                        countdownStore.setCountdownComplete(true);
                        clearInterval(timer);
                    }
                    setTime(tmp);
                }, 1000);
                return () => clearInterval(timer);
            } else {
                onComplete?.();
                countdownStore.setCountdownComplete(true);
            }
        }, [targetDate, onComplete, isValidDate]);

        const renderTimeSection = (value: string, label: string) => (
            <TimeSection>
                <Box className="number-group">
                    <NumberBlock>
                        <span className="squadaone-font">
                            {value[0] || '0'}
                        </span>
                    </NumberBlock>
                    <NumberBlock>
                        <span className="squadaone-font">
                            {value[1] || '0'}
                        </span>
                    </NumberBlock>
                </Box>
                <Typography className="label">{label}</Typography>
            </TimeSection>
        );

        if (!fontLoaded) {
            return (
                <>
                    <GlobalFontStyles />
                    <div>Loading font...</div>
                </>
            );
        }

        return (
            <>
                <GlobalFontStyles />
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {renderTimeSection(time.days, 'Days')}
                        <Separator />
                        {renderTimeSection(time.hours, 'Hours')}
                        <Separator />
                        {renderTimeSection(time.minutes, 'Minutes')}
                        <Separator />
                        {renderTimeSection(time.seconds, 'Seconds')}
                    </Box>
                </Box>
            </>
        );
    },
);

export default MobileCountDown;
